import { z } from 'zod';
import type { Viewport } from 'reactflow';

// The flow editor's pan/zoom is persisted per resource so a reload (or
// navigating away and back) drops the editor where it was last left rather than
// recentring on the whole graph. Storage is local and per browser on purpose:
// two people editing the same conversation each keep their own last position.
//
// All resources share a single localStorage key holding an array of the most
// recently used entries, oldest dropped once we pass MAX_ENTRIES. A per-key
// scheme would grow without bound (a heavy user opens most conversations over
// time), and one entry is tiny (~90 bytes), so a single capped array keeps the
// footprint trivial without leaving stale keys behind forever.

const STORAGE_KEY = 'flow-editor-viewport';

// A user only ever works across a handful of conversations at once, so this sits
// comfortably above any real working set while still bounding lifetime growth.
const MAX_ENTRIES = 50;

const entrySchema = z.object({
  id: z.string(),
  viewport: z.object({
    x: z.number(),
    y: z.number(),
    zoom: z.number(),
  }),
});

const entriesSchema = z.array(entrySchema);

type ViewportEntry = z.infer<typeof entrySchema>;

// localStorage access can throw (quota exceeded on write, or access denied when
// storage is disabled), so every read and write is guarded. On any failure we
// behave as if nothing was stored: the editor falls back to centring on the
// start node rather than surfacing an error.
function readEntries(): ViewportEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) {
      return [];
    }
    const parsed = entriesSchema.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : [];
  } catch (_) {
    return [];
  }
}

function writeEntries(entries: ViewportEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (_) {
    // Quota exceeded or storage unavailable. Restoring the viewport is a
    // convenience, so silently skip the write rather than disrupt editing.
  }
}

export function loadViewport(resourceId: string): Viewport | null {
  const entry = readEntries().find((candidate) => candidate.id === resourceId);
  return entry ? entry.viewport : null;
}

export function saveViewport(resourceId: string, viewport: Viewport) {
  // Move this resource to the front as the most recently used, then trim the
  // oldest entries off the end so the array never exceeds MAX_ENTRIES.
  const others = readEntries().filter((entry) => entry.id !== resourceId);
  const entries = [{ id: resourceId, viewport }, ...others].slice(0, MAX_ENTRIES);
  writeEntries(entries);
}
